import { useEffect, useRef, useState } from "react";
import { Camera, ImagePlus, Loader2, AlertTriangle, CheckCircle2, RotateCcw, Pencil, CloudUpload } from "lucide-react";
import api, { apiErr } from "../../lib/api";

import UI from "../../lib/uiTranslations.json";
const L = {
  it: { title: "Patente di guida (fronte e retro)", front: "Fronte", back: "Retro", saved: "Foto salvata", idp: "Permesso internazionale", cam: "Scatta foto", gal: "Galleria", nowBack: "Ora fotografa il retro",
    reading: "Sto leggendo la patente…", uploading: "Caricamento foto…", offline: "Connessione assente: le foto restano qui e verranno inviate appena torna la rete.",
    ok: "Patente letta! Controlla i dati e conferma.", fail: "Lettura automatica non disponibile: inserisci i dati guardando la patente.",
    blur: "Foto poco nitida o tagliata: se puoi, rifalla per una lettura migliore.", check: "Da controllare", retry: "Rifai le foto", edit: "Modifica",
    go: "Conferma e continua", skip: "Continua, la verificheremo noi", slow: "La lettura sta richiedendo più tempo del solito. Le foto sono salvate: puoi proseguire, il nostro staff verificherà la patente.",
    skipped: "Foto salvate: la patente sarà verificata dal nostro staff prima della consegna.", hasIdp: "Ho anche un permesso internazionale di guida",
    noExp: "La mia patente non ha data di scadenza (validità illimitata)",
    expired: "La patente è scaduta: non è possibile noleggiare il mezzo.", short: "La patente scade prima della fine del noleggio.",
    privacy: "Le foto sono salvate in modo cifrato e visibili solo allo staff Amalfi New Service per preparare il contratto.",
    f: { first_name: "Nome (2)", last_name: "Cognome (1)", license_number: "Numero patente (5)", issue_date: "Rilascio (4a)", license_expiry: "Scadenza (4b)", country: "Paese", birth_date: "Nascita (3)", categories: "Categorie (9)" } },
  en: { title: "Driving licence (front and back)", front: "Front", back: "Back side", saved: "Photo saved", idp: "International permit", cam: "Take photo", gal: "Gallery", nowBack: "Now take the back",
    reading: "Reading your licence…", uploading: "Uploading photo…", offline: "No connection: your photos stay here and will be sent as soon as you are back online.",
    ok: "Licence read! Check the details and confirm.", fail: "Automatic reading unavailable: please enter the details from your licence.",
    blur: "Photo blurry or cropped: if you can, retake it for a better reading.", check: "Please check", retry: "Retake photos", edit: "Edit",
    go: "Confirm and continue", skip: "Continue, we will check it", slow: "Reading is taking longer than usual. Your photos are saved: you can continue and our staff will check your licence.",
    skipped: "Photos saved: our staff will check your licence before handing over the vehicle.", hasIdp: "I also have an International Driving Permit",
    noExp: "My licence has no expiry date (unlimited validity)",
    expired: "Your licence has expired: the vehicle cannot be rented.", short: "Your licence expires before the end of the rental.",
    privacy: "Photos are stored encrypted and visible only to Amalfi New Service staff to prepare the contract.",
    f: { first_name: "First name (2)", last_name: "Surname (1)", license_number: "Licence number (5)", issue_date: "Issue date (4a)", license_expiry: "Expiry date (4b)", country: "Country", birth_date: "Date of birth (3)", categories: "Categories (9)" } },
};
const DATE = ["issue_date", "license_expiry", "birth_date"];
const CRIT = ["first_name", "last_name", "license_number", "license_expiry"];
const KEYS = ["first_name", "last_name", "birth_date", "birth_place", "issue_date", "license_expiry", "license_number", "country", "categories", "restrictions"];
const SLOW_MS = 12000;
const critOf = (l) => CRIT.filter((k) => !(k === "license_expiry" && l?.no_expiry));
export const licenseOk = (f, endDay, today) => {
  if (!f.license_scan_id || !f.license_ok) return false;
  const e = f.license?.license_expiry;
  if (e && (e < today || (endDay && e < endDay))) return false;
  return f.license_skip || critOf(f.license).every((k) => (f.license?.[k] || "").trim());
};

async function compressImg(file) {
  try {
    const bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
    const s = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
    const c = document.createElement("canvas");
    c.width = Math.round(bmp.width * s); c.height = Math.round(bmp.height * s);
    c.getContext("2d").drawImage(bmp, 0, 0, c.width, c.height);
    const blob = await new Promise((r) => c.toBlob(r, "image/jpeg", 0.85));
    return blob && blob.size < file.size ? new File([blob], "licence.jpg", { type: "image/jpeg" }) : file;
  } catch { return file; }
}
const waitOnline = () => (navigator.onLine ? Promise.resolve() : new Promise((r) => window.addEventListener("online", r, { once: true })));

function Slot({ label, item, onPick, T, id, camRef, hint }) {
  const gal = useRef(null);
  return (
    <div className={`flex-1 min-w-[140px] rounded-xl border bg-white p-2.5 transition-colors ${hint ? "border-[#5B6B3A] ring-2 ring-[#5B6B3A]/30" : "border-[#E0D5BF]"}`} data-testid={`lic-slot-${id}`}>
      <p className="text-xs font-semibold mb-1.5 flex items-center gap-1">{label}{id !== "idp" && " *"}
        {item?.st === "up" && <Loader2 size={12} className="animate-spin" data-testid={`lic-uploading-${id}`} />}{item?.st === "ok" && <CloudUpload size={12} className="text-emerald-600" data-testid={`lic-uploaded-${id}`} />}</p>
      {item?.saved ? <div className="h-24 rounded-lg bg-emerald-50 mb-2 grid place-items-center text-[11px] text-emerald-800 font-semibold" data-testid={`lic-saved-${id}`}><CheckCircle2 size={18} />{T.saved}</div>
        : item ? <img src={item.url} alt={label} className="w-full h-24 object-cover rounded-lg mb-2" data-testid={`lic-preview-${id}`} /> : <div className="h-24 rounded-lg bg-[#F5EFE3] mb-2 grid place-items-center text-[11px] text-[#5B6B3A] font-semibold">{hint || ""}</div>}
      <div className="flex gap-1.5">
        <button type="button" onClick={() => camRef.current?.click()} className="flex-1 min-h-[44px] rounded-lg bg-[#2F3A1F] text-white text-xs font-semibold inline-flex items-center justify-center gap-1" data-testid={`lic-cam-${id}`}><Camera size={14} />{T.cam}</button>
        <button type="button" onClick={() => gal.current?.click()} className="flex-1 min-h-[44px] rounded-lg border border-[#E0D5BF] text-xs font-semibold inline-flex items-center justify-center gap-1" data-testid={`lic-gal-${id}`}><ImagePlus size={14} />{T.gal}</button>
      </div>
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { onPick(e.target.files?.[0], true); e.target.value = ""; }} data-testid={`lic-cam-input-${id}`} />
      <input ref={gal} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" className="hidden" onChange={(e) => { onPick(e.target.files?.[0], false); e.target.value = ""; }} data-testid={`lic-gal-input-${id}`} />
    </div>
  );
}

function Fields({ lic, res, T, setL, setNoExp, need, open, setOpen }) {
  const keys = ["first_name", "last_name", "license_number", "issue_date", ...(lic.no_expiry ? [] : ["license_expiry"]), "country", ...need.filter((k) => !T.f[k] || !["first_name", "last_name", "license_number", "issue_date", "license_expiry", "country"].includes(k))];
  return (
    <div className="rounded-2xl border border-[#E0D5BF] bg-white p-3 grid grid-cols-2 gap-x-3 gap-y-2" data-testid="lic-compact">
      {keys.map((k) => {
        const unc = res?.uncertain?.includes(k) || !(lic[k] || "").trim();
        const edit = open.includes(k) || unc;
        return (
          <div key={k} className={k === "license_number" ? "col-span-2" : ""}>
            <span className={`text-[11px] font-semibold ${unc ? "text-amber-700" : "text-[#6B6B5E]"}`}>{T.f[k]}{unc ? ` · ${T.check}` : ""}</span>
            {edit ? <input type={DATE.includes(k) ? "date" : "text"} className={`pp-in !py-2 ${unc ? "!border-amber-400 !bg-amber-50" : ""}`} value={lic[k] || ""} onChange={(e) => setL(k, e.target.value)} data-testid={`lic-field-${k}`} />
              : <button type="button" className="w-full text-left text-[15px] font-semibold text-[#1F3B57] flex items-center justify-between gap-2 min-h-[36px]" onClick={() => setOpen([...open, k])} data-testid={`lic-value-${k}`}>
                <span className="truncate">{lic[k]}</span><Pencil size={13} className="text-[#6B6B5E] shrink-0" aria-label={T.edit} /></button>}
          </div>
        );
      })}
      <label className={`col-span-2 flex items-center gap-2 text-sm ${res?.uncertain?.includes("no_expiry") ? "text-amber-700" : ""}`}>
        <input type="checkbox" className="w-5 h-5" checked={!!lic.no_expiry} onChange={(e) => setNoExp(e.target.checked)} data-testid="lic-no-expiry" />{T.noExp || L.en.noExp}
      </label>
    </div>
  );
}

export default function LicenseCapture({ slug, scanUrl, lang, f, set, endDay, today, onDone, need = [] }) {
  const T = L[lang] || (UI[lang] ? { ...L.en, ...UI[lang].license } : L.en);
  const base = scanUrl ? scanUrl.replace(/\/license-scan$/, "") : `/public/pages/${slug}`;
  const store = `lic:${base}`;
  const [img, setImg] = useState({});
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState([]);
  const [slow, setSlow] = useState(false);
  const [idp, setIdp] = useState(false);
  const [off, setOff] = useState(!navigator.onLine);
  const sid = useRef(f.license_scan_id || null);
  const chain = useRef(Promise.resolve());
  const edited = useRef(new Set());
  const t = useRef({});
  const backCam = useRef(null), frontCam = useRef(null), idpCam = useRef(null);
  const lic = f.license || {};
  const licRef = useRef(lic);
  licRef.current = lic;

  useEffect(() => {
    const on = () => setOff(false), offH = () => setOff(true);
    window.addEventListener("online", on); window.addEventListener("offline", offH);
    const saved = !sid.current && sessionStorage.getItem(store);
    if (saved) { sid.current = saved; poll(); }
    else if (sid.current) poll();
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", offH); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = (d) => {
    setRes(d);
    const cur = licRef.current;
    const lic = Object.fromEntries(KEYS.map((k) => [k, edited.current.has(k) ? (cur[k] ?? "") : (d[k] || cur[k] || "")]));
    lic.no_expiry = edited.current.has("no_expiry") ? !!cur.no_expiry : !!(d.no_expiry || cur.no_expiry) && !lic.license_expiry;
    set({ license_scan_id: d.scan_id, license: lic });
  };
  const timer = useRef(null);
  const poll = async () => {
    clearTimeout(timer.current);
    if (!sid.current) return;
    try {
      const { data } = await api.get(`${base}/license-scan/${sid.current}`);
      apply(data);
      if (data.ready || data.ocr_failed) {
        if (t.current.last && !t.current.sent) {
          t.current.sent = true;
          const now = performance.now();
          api.post(`${base}/license-scan/${data.scan_id}/timing`, { upload_ms: Math.round((t.current.up || now) - t.current.last), shown_ms: Math.round(now - t.current.last), total_ms: Math.round(now - t.current.first) }).catch(() => {});
        }
        return;
      }
    } catch (e) { if (e?.response?.status === 404) { sessionStorage.removeItem(store); sid.current = null; return; } }
    timer.current = setTimeout(poll, 900);
  };
  useEffect(() => () => clearTimeout(timer.current), []);

  const upload = (side, file) => {
    const key = crypto.randomUUID();
    chain.current = chain.current.then(async () => {
      for (let i = 0; i < 4; i++) {
        await waitOnline();
        const fd = new FormData();
        fd.append("side", side); fd.append("file", await compressImg(file)); fd.append("scan_id", sid.current || ""); fd.append("upload_key", key);
        try {
          const { data } = await api.post(`${base}/license-photo`, fd, { timeout: 45000 });
          sid.current = data.scan_id; sessionStorage.setItem(store, data.scan_id);
          setImg((o) => ({ ...o, [side]: { ...o[side], st: "ok" } }));
          return data;
        } catch (e) {
          const s = e?.response?.status;
          if (s === 409) { sid.current = null; sessionStorage.removeItem(store); }
          if (s && s < 500 && s !== 409 && s !== 429) { setErr(apiErr(e)); setImg((o) => ({ ...o, [side]: null })); return null; }
          await new Promise((r) => setTimeout(r, 800 * (i + 1)));
        }
      }
      setErr(apiErr({ message: "Upload failed" })); setImg((o) => ({ ...o, [side]: { ...o[side], st: "err" } }));
      return null;
    });
    return chain.current;
  };

  const pick = (side) => async (file, fromCam) => {
    if (!file) return;
    setErr(""); setSlow(false);
    const now = performance.now();
    t.current = { ...t.current, first: t.current.first || now, last: now, sent: false, up: null };
    setImg((o) => ({ ...o, [side]: { url: URL.createObjectURL(file), st: "up" } }));
    if (side !== "idp") { set({ license_ok: false, license_skip: false }); setRes((r) => (r ? { ...r, ready: false } : r)); }
    if (side === "front" && fromCam && !img.back) setTimeout(() => backCam.current?.click(), 350);
    const d = await upload(side, file);
    if (!d) return;
    t.current.up = performance.now();
    apply(d);
    poll();
  };

  const both = (img.front?.st === "ok" && img.back?.st === "ok") || (!img.front && !!res?.sides?.front && !!res?.sides?.back);
  useEffect(() => {
    if (!both || res?.ready) { setSlow(false); return; }
    const h = setTimeout(() => setSlow(true), SLOW_MS);
    return () => clearTimeout(h);
  }, [both, res?.ready]);

  const setL = (k, v) => { edited.current.add(k); setOpen((o) => (o.includes(k) ? o : [...o, k])); set({ license: { ...lic, [k]: v, ...(k === "license_expiry" && v ? { no_expiry: false } : {}) }, license_ok: false, license_skip: false }); };
  const setNoExp = (v) => { edited.current.add("no_expiry"); edited.current.add("license_expiry"); set({ license: { ...lic, no_expiry: v, license_expiry: v ? "" : lic.license_expiry }, license_ok: false, license_skip: false }); };
  const reset = () => { setImg({}); setRes(null); setOpen([]); edited.current = new Set(); sid.current = null; sessionStorage.removeItem(store); set({ license_scan_id: null, license_ok: false, license_skip: false, license: {} }); };
  const bad = !!(res && (res.readable === false || res.complete === false || res.blurry?.length));
  const expired = lic.license_expiry && lic.license_expiry < today;
  const short = !expired && lic.license_expiry && endDay && lic.license_expiry < endDay;
  const restored = !!res && !img.front;
  const anyData = res && CRIT.some((k) => lic[k]);
  const showFields = res && (res.ready || res.ocr_failed || anyData || slow);
  const canGo = both && critOf(lic).concat(need).every((k) => (lic[k] || "").trim()) && !expired && !short;
  const confirm = () => { set({ license_ok: true, license_skip: false }); onDone?.(); };
  const skip = () => { set({ license_ok: true, license_skip: true }); onDone?.(); };
  const reading = res && !res.ready && !res.ocr_failed && (img.front?.st === "ok" || restored);
  const saved = (k) => (!img[k] && res?.sides?.[k] ? { url: "", st: "ok", saved: true } : img[k]);

  return (
    <div className="space-y-3 ph-no-capture" data-testid="license-capture">
      <p className="text-[11px] tracking-[0.2em] text-[#5B6B3A] font-semibold">{T.title.toUpperCase()}</p>
      <div className="flex flex-wrap gap-2.5">
        <Slot label={T.front} item={saved("front")} onPick={pick("front")} T={T} id="front" camRef={frontCam} />
        <Slot label={T.back} item={saved("back")} onPick={pick("back")} T={T} id="back" camRef={backCam} hint={img.front && !img.back ? T.nowBack : ""} />
      </div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="w-5 h-5" checked={idp} onChange={(e) => setIdp(e.target.checked)} data-testid="lic-idp-toggle" />{T.hasIdp}</label>
      {idp && <Slot label={T.idp} item={img.idp} onPick={pick("idp")} T={T} id="idp" camRef={idpCam} />}
      {off && <p className="text-sm text-amber-800" data-testid="lic-offline">{T.offline}</p>}
      {err && <p className="text-sm text-rose-700" data-testid="lic-error">{err}</p>}
      {reading && <p className="text-sm flex items-center gap-2" data-testid="lic-reading"><Loader2 size={16} className="animate-spin" />{T.reading}</p>}
      {res?.ocr_failed && <p className="text-sm text-amber-800 flex gap-2" data-testid="lic-ocr-failed"><AlertTriangle size={16} className="shrink-0" />{T.fail}</p>}
      {res?.ready && !bad && <p className="text-sm text-[#2F3A1F] flex gap-2" data-testid="lic-ocr-ok"><CheckCircle2 size={16} className="shrink-0 text-emerald-600" />{T.ok}</p>}
      {res?.ready && bad && <p className="text-sm text-amber-800 flex items-center gap-2 flex-wrap" data-testid="lic-blurry"><AlertTriangle size={16} />{T.blur}<button type="button" onClick={reset} className="underline font-semibold inline-flex items-center gap-1" data-testid="lic-retry"><RotateCcw size={13} />{T.retry}</button></p>}
      {showFields && <Fields lic={lic} res={res} T={T} setL={setL} setNoExp={setNoExp} need={need} open={open} setOpen={setOpen} />}
      {expired && <p className="text-sm font-semibold text-rose-700" data-testid="lic-expired">{T.expired}</p>}
      {short && <p className="text-sm font-semibold text-rose-700" data-testid="lic-short">{T.short}</p>}
      {f.license_ok && f.license_skip && <p className="text-sm text-[#2F3A1F]" data-testid="lic-skipped">{T.skipped}</p>}
      {showFields && !expired && !short && (
        <div className="space-y-2">
          <button type="button" className="pp-btn w-full justify-center disabled:opacity-40" disabled={!canGo} onClick={confirm} data-testid="lic-confirm-continue">{f.license_ok && !f.license_skip ? <><CheckCircle2 size={16} />{T.go}</> : T.go}</button>
          {(slow || res?.ocr_failed) && !canGo && both && <><p className="text-xs text-[#6B6B5E]" data-testid="lic-slow">{T.slow}</p>
            <button type="button" className="w-full min-h-[48px] rounded-full border border-[#1F3B57] text-[#1F3B57] text-sm font-semibold" onClick={skip} data-testid="lic-skip">{T.skip}</button></>}
        </div>
      )}
      {!showFields && both && slow && <div className="space-y-2"><p className="text-xs text-[#6B6B5E]" data-testid="lic-slow">{T.slow}</p>
        <button type="button" className="w-full min-h-[48px] rounded-full border border-[#1F3B57] text-[#1F3B57] text-sm font-semibold" onClick={skip} data-testid="lic-skip">{T.skip}</button></div>}
      <p className="text-[11px] text-[#6B6B5E]">{T.privacy}</p>
    </div>
  );
}
